<template>
  <div id="reckon-way-people" class="people-page">
    <CultureNav />
    <CultureHero
      title="Meet the Heroes"
      summary="Meet the real people behind the work and the superhero identities that celebrate the strengths they bring to the Reckon Crew."
      eyebrow="The Reckon Crew"
    />

    <main>
      <v-container>
        <section class="passport-section">
          <div class="section-heading">
            <p class="kicker">Different Strengths. One Crew.</p>
            <h2>Every team needs different strengths.</h2>
            <p>Some of us build. Some architect. Some test, automate, analyze, organize, teach, or see the thing everyone else missed.</p>
            <p>Put those abilities together and something interesting happens.</p>
          </div>

          <div class="passport-grid">
            <article
              v-for="passport in activePassports"
              :key="passport.slug"
              class="passport-card"
              :class="{ 'passport-card--interactive': heroProfile(passport) }"
            >
              <div v-if="heroProfile(passport)" class="portrait-flip">
                <button
                  type="button"
                  class="portrait-flip__button"
                  :class="{ 'is-flipped': isFlipped(passport) }"
                  :aria-label="flipLabel(passport)"
                  :aria-pressed="isFlipped(passport) ? 'true' : 'false'"
                  @click="toggleCard(passport)"
                >
                  <span class="portrait-flip__face portrait-flip__face--front">
                    <img
                      :src="realImage(passport) | mediaSrc"
                      :alt="`${passport.fullName} headshot`"
                      loading="lazy"
                    />
                    <span class="passport-number">{{ passport.passportNumber }}</span>
                  </span>
                  <span class="portrait-flip__face portrait-flip__face--back">
                    <img
                      :src="heroImageFor(passport) | mediaSrc"
                      :alt="heroProfile(passport).artAlt"
                      loading="lazy"
                    />
                  </span>
                </button>
              </div>
              <div v-else class="portrait-wrap">
                <img :src="passport.headshot | mediaSrc" :alt="`${passport.fullName} headshot`" loading="lazy" />
                <span class="passport-number">{{ passport.passportNumber }}</span>
              </div>
              <div
                v-if="hasCardAwards(passport)"
                class="card-awards"
                aria-label="Crew member awards"
              >
                <span
                  v-if="passport.chickFilAFridayTrophy"
                  class="card-award card-award--trophy"
                  :class="`card-award--${passport.chickFilAFridayTrophy.tone}`"
                  role="img"
                  tabindex="0"
                  :aria-label="chickFilAAwardLabel(passport.chickFilAFridayTrophy)"
                >
                  <v-icon small aria-hidden="true">mdi-trophy</v-icon>
                  <strong aria-hidden="true">{{ passport.chickFilAFridayTrophy.rank }}</strong>
                  <span class="card-award__tooltip" aria-hidden="true">
                    <strong>Chick-fil-A Friday · {{ passport.chickFilAFridayTrophy.place }}</strong>
                    <small>
                      {{ passport.chickFilAFridayTrophy.attendedEvents }} of
                      {{ passport.chickFilAFridayTrophy.eligibleEvents }} Fridays ·
                      {{ passport.chickFilAFridayTrophy.attendancePercentage }}%
                    </small>
                  </span>
                </span>
                <span
                  v-for="award in passport.awards"
                  :key="award.id"
                  class="card-award"
                  :class="`card-award--${award.tone}`"
                  role="img"
                  tabindex="0"
                  :aria-label="passportAwardLabel(award)"
                >
                  <span
                    v-if="award.artType === 'championship-belt'"
                    class="card-award__belt"
                    aria-hidden="true"
                  >R</span>
                  <img
                    v-else-if="award.image"
                    :src="award.image | mediaSrc"
                    alt=""
                    aria-hidden="true"
                  />
                  <v-icon v-else small aria-hidden="true">{{ award.icon }}</v-icon>
                  <span class="card-award__tooltip" aria-hidden="true">
                    <span>{{ award.category }}</span>
                    <strong>{{ award.name }} · {{ award.recognitionLabel }}</strong>
                    <small>{{ award.description }}</small>
                  </span>
                </span>
              </div>
              <div class="passport-copy">
                <p>Reckon Passport</p>
                <div class="passport-card__identity">
                  <h3>{{ passport.fullName }}</h3>
                  <a
                    v-if="passport.linkedInUrl"
                    :href="passport.linkedInUrl"
                    class="linkedin-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    :aria-label="`View ${passport.fullName} on LinkedIn`"
                    :title="`View ${passport.fullName} on LinkedIn`"
                  >
                    <v-icon small>mdi-linkedin</v-icon>
                  </a>
                </div>
                <span class="role">{{ passport.title }}</span>
                <dl>
                  <div>
                    <dt>Journeys</dt>
                    <dd>{{ passport.stats.reckonTrips }}</dd>
                  </div>
                  <div>
                    <dt>Badges</dt>
                    <dd>{{ passport.stats.meritBadgesEarned }}</dd>
                  </div>
                  <div>
                    <dt>Level</dt>
                    <dd>{{ passport.adventureLevel }}</dd>
                  </div>
                </dl>
                <div class="passport-card__actions">
                  <router-link
                    v-if="heroProfile(passport) && isFlipped(passport)"
                    :to="heroRoute(passport)"
                    class="card-action"
                  >
                    Origin Story <v-icon small>mdi-arrow-right</v-icon>
                  </router-link>
                  <router-link :to="passportRoute(passport)" class="card-action card-action--muted">
                    Reckon Passport <v-icon small>mdi-arrow-right</v-icon>
                  </router-link>
                </div>
              </div>
            </article>

            <article
              v-for="crewMember in pendingCrew"
              :key="crewMember.id"
              class="passport-card passport-card--pending"
            >
              <div class="portrait-wrap portrait-wrap--pending">
                <img
                  :src="crewMember.image | mediaSrc"
                  :alt="crewMember.alt"
                  loading="lazy"
                />
              </div>
              <div class="passport-copy">
                <p>New Reckon Hire</p>
                <h3>Profile Coming Soon</h3>
                <span class="role">Hero identity in development</span>
                <div class="pending-status">
                  <v-icon small>mdi-account-clock-outline</v-icon>
                  Crew profile in progress
                </div>
              </div>
            </article>
          </div>
        </section>

        <section v-if="alumniPassports.length" class="passport-section alumni-section">
          <div class="section-heading">
            <p class="kicker">Part of the Story</p>
            <h2>Crew Alumni</h2>
            <p>Their Reckon passports preserve the experiences and traditions they shared with the Crew.</p>
          </div>
          <div class="passport-grid">
            <router-link
              v-for="passport in alumniPassports"
              :key="passport.slug"
              :to="passportRoute(passport)"
              class="passport-card passport-card--alumni"
            >
              <div class="portrait-wrap">
                <img :src="passport.headshot | mediaSrc" :alt="`${passport.fullName} headshot`" loading="lazy" />
                <span class="passport-number">{{ passport.passportNumber }}</span>
              </div>
              <div class="passport-copy">
                <p>Reckon Passport · Alumni</p>
                <h3>{{ passport.fullName }}</h3>
                <span class="role">{{ passport.title }}</span>
                <dl>
                  <div><dt>Journeys</dt><dd>{{ passport.stats.reckonTrips }}</dd></div>
                  <div><dt>Badges</dt><dd>{{ passport.stats.meritBadgesEarned }}</dd></div>
                  <div><dt>Level</dt><dd>{{ passport.adventureLevel }}</dd></div>
                </dl>
                <span class="open-passport">Open Passport <v-icon small>mdi-arrow-right</v-icon></span>
              </div>
            </router-link>
          </div>
        </section>
      </v-container>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import { mediaRef } from '@/services/mediaAssets';
import AppFooter from '../components/Footer.vue';
import CultureHero from '../components/reckon-way/culture/CultureHero.vue';
import CultureNav from '../components/reckon-way/culture/CultureNav.vue';
import { heroProfileByCrewSlug } from '../data/heroProfiles';
import { passportList } from '../data/passports';

const pendingCrew = [
  {
    id: 'new-hire-female',
    image: mediaRef('heroes/coming_soon_reckon_crew.png'),
    alt: 'New Reckon crew member profile coming soon',
  },
  {
    id: 'new-hire-male',
    image: mediaRef('heroes/coming_soon_reckon_crew_male.png'),
    alt: 'New Reckon crew member profile coming soon',
  },
];

export default {
  name: 'CulturePeopleView',
  components: {
    AppFooter, CultureHero, CultureNav,
  },
  data: () => ({
    passports: passportList,
    flippedCards: {},
    pendingCrew,
  }),
  computed: {
    activePassports() {
      return this.passports.filter((passport) => passport.status === 'active');
    },
    alumniPassports() {
      return this.passports.filter((passport) => passport.status === 'alumni');
    },
  },
  methods: {
    heroProfile(passport) {
      return heroProfileByCrewSlug[passport.slug] || null;
    },
    heroRoute(passport) {
      return { name: 'hero-profile', params: { heroSlug: this.heroProfile(passport).slug } };
    },
    passportRoute(passport) {
      return { name: 'passport', params: { employeeSlug: passport.slug } };
    },
    isFlipped(passport) {
      return Boolean(this.flippedCards[passport.slug]);
    },
    toggleCard(passport) {
      this.$set(this.flippedCards, passport.slug, !this.isFlipped(passport));
    },
    flipLabel(passport) {
      return this.isFlipped(passport)
        ? `Show ${passport.fullName}'s real-world identity`
        : `Reveal ${passport.fullName}'s superhero identity, ${this.heroProfile(passport).heroName}`;
    },
    realImage(passport) {
      const profile = this.heroProfile(passport);
      return profile && profile.realImage ? profile.realImage : passport.headshot;
    },
    heroImageFor(passport) {
      const profile = this.heroProfile(passport);
      return [profile.heroImage, profile.placeholderImage];
    },
    hasCardAwards(passport) {
      return Boolean(passport.chickFilAFridayTrophy || passport.awards.length);
    },
    chickFilAAwardLabel(trophy) {
      return `Chick-fil-A Friday, ${trophy.place}, ${trophy.attendedEvents} of ${trophy.eligibleEvents} Fridays, ${trophy.attendancePercentage}% attendance`;
    },
    passportAwardLabel(award) {
      return `${award.category}, ${award.name}, ${award.recognitionLabel}. ${award.description}`;
    },
  },
};
</script>

<style scoped>
.people-page,
.people-page main {
  background: #050505;
}

.people-page main {
  padding: 64px 0 80px;
}

.passport-section + .passport-section {
  border-top: 1px solid #292929;
  margin-top: 68px;
  padding-top: 58px;
}

.section-heading {
  margin-bottom: 28px;
  max-width: 780px;
}

.kicker,
.passport-copy > p {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .76rem;
  font-weight: 900;
  letter-spacing: .04em;
  margin: 0 0 7px;
  text-transform: uppercase;
}

h2 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: clamp(2.2rem, 5vw, 3.4rem);
  line-height: 1;
  margin: 0;
}

.section-heading > p:not(.kicker) {
  color: #aaa;
  font-size: 1.05rem;
  line-height: 1.65;
  margin: 14px 0 0;
}

.passport-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.passport-card {
  background: linear-gradient(145deg, #171717, #0d0d0d);
  border: 1px solid #303030;
  border-radius: 12px;
  color: inherit;
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
  position: relative;
  text-decoration: none;
  transition: border-color 180ms ease, transform 180ms ease;
}

.passport-card--alumni:hover,
.passport-card--alumni:focus-visible {
  border-color: #e23232;
  transform: translateY(-4px);
}

.passport-card--interactive {
  overflow: visible;
}

.portrait-flip {
  aspect-ratio: 3 / 4;
  background: #111;
  border-radius: 11px 11px 0 0;
  perspective: 1200px;
  position: relative;
  transition: box-shadow 180ms ease, transform 180ms ease;
  z-index: 1;
}

.portrait-flip:hover,
.portrait-flip:focus-within {
  box-shadow: 0 0 0 3px rgba(226, 50, 50, 0.82), 0 18px 34px rgba(0, 0, 0, 0.48);
  transform: translateY(-6px) scale(1.012);
  z-index: 2;
}

.portrait-flip__button {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: block;
  height: 100%;
  padding: 0;
  position: relative;
  text-align: left;
  transform-style: preserve-3d;
  transition: transform 520ms cubic-bezier(.2, .7, .2, 1);
  width: 100%;
}

.portrait-flip__button.is-flipped {
  transform: rotateY(180deg);
}

.portrait-flip__button:focus-visible {
  outline: 3px solid #fff;
  outline-offset: -5px;
}

.portrait-flip__face {
  backface-visibility: hidden;
  background: #111;
  inset: 0;
  overflow: hidden;
  position: absolute;
}

.portrait-flip__face::after {
  background: linear-gradient(to top, rgba(0, 0, 0, .82), transparent 48%);
  content: '';
  inset: 0;
  pointer-events: none;
  position: absolute;
}

.portrait-flip__face--back {
  transform: rotateY(180deg);
}

.portrait-flip__face img {
  height: 100%;
  object-fit: contain;
  object-position: center top;
  width: 100%;
}

.portrait-wrap {
  aspect-ratio: 3 / 4;
  background: #111;
  overflow: hidden;
  position: relative;
}

.portrait-wrap::after {
  background: linear-gradient(to top, rgba(0, 0, 0, .7), transparent 45%);
  bottom: 0;
  content: '';
  inset: 0;
  position: absolute;
}

.portrait-wrap img {
  height: 100%;
  object-fit: contain;
  object-position: center top;
  width: 100%;
}

.portrait-wrap--pending::after { display: none; }

.passport-card--pending {
  border-style: dashed;
}

.pending-status {
  align-items: center;
  border-top: 1px solid #2b2b2b;
  color: #b8b8b8;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  gap: 7px;
  margin-top: 20px;
  padding-top: 15px;
  text-transform: uppercase;
}

.pending-status .v-icon { color: #e23232; }

.passport-number {
  bottom: 13px;
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: .74rem;
  font-weight: 800;
  left: 15px;
  position: absolute;
  text-transform: uppercase;
  z-index: 1;
}

.card-awards {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 7px;
  justify-content: flex-start;
  position: absolute;
  right: 13px;
  top: 13px;
  z-index: 4;
}

.card-award {
  --card-award-color: #f6c94f;
  align-items: center;
  background: rgba(12, 12, 12, .9);
  border: 1px solid var(--card-award-color);
  border-radius: 50%;
  box-shadow: 0 4px 14px rgba(0, 0, 0, .46);
  color: #fff;
  display: inline-flex;
  flex: 0 0 38px;
  height: 38px;
  justify-content: center;
  position: relative;
  transition: box-shadow 160ms ease, transform 160ms ease;
  width: 38px;
}

.card-award:hover,
.card-award:focus-visible {
  box-shadow: 0 0 0 2px #050505, 0 0 0 4px var(--card-award-color), 0 8px 18px rgba(0, 0, 0, .58);
  outline: 0;
  transform: translateY(-2px);
}

.card-award--red { --card-award-color: #f04444; }
.card-award--champion { --card-award-color: #ffd84f; }
.card-award--silver { --card-award-color: #c7d0da; }
.card-award--bronze { --card-award-color: #c88752; }
.card-award--gold { --card-award-color: #f6c94f; }

.card-award--trophy {
  background: rgba(12, 12, 12, .94);
  color: var(--card-award-color);
}

.card-award--trophy .v-icon {
  color: var(--card-award-color) !important;
  font-size: 1.35rem !important;
}

.card-award--trophy > strong {
  align-items: center;
  background: #111;
  border: 1px solid var(--card-award-color);
  border-radius: 50%;
  bottom: -4px;
  color: #fff;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: .68rem;
  height: 18px;
  justify-content: center;
  position: absolute;
  right: -4px;
  width: 18px;
}

.card-award > img {
  background: #fff;
  border-radius: 50%;
  height: 32px;
  object-fit: contain;
  padding: 2px;
  width: 32px;
}

.card-award > .v-icon { color: var(--card-award-color); }

.card-award__belt {
  align-items: center;
  background: radial-gradient(circle, #fff09a 0, #d7a910 66%, #8a6500 100%);
  border: 2px solid #ffe278;
  border-radius: 50%;
  color: #d71920;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  height: 25px;
  justify-content: center;
  width: 25px;
}

.card-award__tooltip {
  background: #111;
  border: 1px solid var(--card-award-color);
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, .62);
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0;
  padding: 10px 12px;
  pointer-events: none;
  position: absolute;
  right: 0;
  text-align: left;
  top: calc(100% + 9px);
  transform: translateY(-3px);
  transition: opacity 140ms ease, transform 140ms ease;
  visibility: hidden;
  width: 245px;
  z-index: 6;
}

.card-award__tooltip > span {
  color: #aaa;
  font-size: .63rem;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.card-award__tooltip strong {
  color: var(--card-award-color);
  font-family: "Oswald", sans-serif;
  font-size: .9rem;
  line-height: 1.25;
}

.card-award__tooltip small {
  color: #c5c5c5;
  font-size: .7rem;
  line-height: 1.4;
}

.card-award:hover .card-award__tooltip,
.card-award:focus-visible .card-award__tooltip {
  opacity: 1;
  transform: translateY(0);
  visibility: visible;
}

.passport-copy {
  display: flex;
  flex-direction: column;
  padding: 22px;
}

.passport-copy h3 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.75rem;
  line-height: 1;
  margin: 0;
}

.passport-card__identity {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.linkedin-link {
  align-items: center;
  background: #0a66c2;
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 6px;
  display: inline-flex;
  flex: 0 0 auto;
  height: 30px;
  justify-content: center;
  text-decoration: none;
  transition: background-color 160ms ease, transform 160ms ease;
  width: 30px;
}

.linkedin-link .v-icon { color: #fff; }
.linkedin-link:hover {
  background: #0878d8;
  transform: translateY(-1px);
}
.linkedin-link:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 3px;
}

.role {
  color: #9e9e9e;
  display: block;
  font-size: .86rem;
  margin-top: 7px;
}

dl {
  border-bottom: 1px solid #2b2b2b;
  border-top: 1px solid #2b2b2b;
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, 1fr);
  margin: 20px 0;
  padding: 15px 0;
}

dt {
  color: #777;
  font-size: .62rem;
  font-weight: 900;
  text-transform: uppercase;
}

dd {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1rem;
  margin: 3px 0 0;
}

.open-passport {
  align-items: center;
  color: #fff;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  gap: 5px;
  margin-top: auto;
  text-transform: uppercase;
}

.open-passport .v-icon { color: #e23232; }
.passport-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: auto;
}

.card-action {
  align-items: center;
  color: #fff;
  display: inline-flex;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  gap: 5px;
  text-decoration: none;
  text-transform: uppercase;
}

.card-action .v-icon { color: #e23232; }
.card-action--muted { color: #b8b8b8; }
.card-action:hover { color: #e23232; }
.card-action:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 4px;
}
.passport-card--alumni { opacity: .84; }

@media (max-width: 960px) {
  .passport-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 600px) {
  .people-page main { padding-top: 46px; }
  .passport-grid { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .passport-card,
  .card-award,
  .card-award__tooltip,
  .portrait-flip,
  .portrait-flip__button { transition: none; }

  .portrait-flip:hover,
  .portrait-flip:focus-within {
    transform: none;
  }
}
</style>
